import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactNode } from "react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { DEFAULT_DAYS, useControls, type Controls } from "./controls";
import { getCode, getCodes, imageUrl, PLACEMENTS, qrCodeKey, qrCodesKey, qrReadMessage, type QrCode, type QrCodeDetailData, type QrCodeSummary } from "./qr-api";
import { CopyButton, MultiChips, Q_CODE, Q_PLACE, Q_RELEASE, Q_STATUS, Q_VIEW, StatusPill, VIEW_RELEASES, str } from "./qr-controls";
import {
  asList,
  changeLine,
  filterCodes,
  placementLabel,
  placementsLabel,
  printedAddress,
  STATUS_ALL,
  STATUS_DEFAULT,
  statusLabel,
  targetLabel,
} from "./qr-format";
import { ReleasesView } from "./QrReleases";
import { ChangeDestinationButton, EditDetails, NewCodeButton, StatusActions, useQrMe } from "./QrWriteActions";

// The QR Codes tab (spec: os-portal specs/qr-codes-module.md, v1 and v2). Backend: transparency
// specs/2026-09-29-qr-codes.md and 2026-09-30-qr-placements-and-releases.md via
// functions/qr-codes/[[path]].ts. This tab configures and publishes; scan figures live on the
// QR Scans tab. Its own URL keys (qr-controls.tsx) never collide with the Split test or Funnels keys.

const VIEW_SPEC: FilterSpec = {
  name: Q_VIEW,
  label: "Show",
  kind: "choice",
  // "Codes" is the empty value: the shell drops an empty filter from the URL.
  options: [
    { value: "", label: "Codes" },
    { value: VIEW_RELEASES, label: "Releases" },
  ],
};

const STATUS_SPEC: FilterSpec = {
  name: Q_STATUS,
  label: "Status",
  kind: "choice",
  options: [
    { value: STATUS_DEFAULT, label: "Live + Paused" },
    { value: "live", label: "Live" },
    { value: "paused", label: "Paused" },
    { value: "retired", label: "Retired" },
    { value: "draft", label: "Draft" },
    { value: STATUS_ALL, label: "All" },
  ],
};

const PLACEMENT_OPTIONS = PLACEMENTS.map((value) => ({ value, label: placementLabel(value) }));

const CODE_COLUMNS: ColumnDef<QrCodeSummary, unknown>[] = [
  { id: "name", header: "Name", accessorFn: (row) => row.label },
  { id: "printed", header: "Printed address", accessorFn: (row) => printedAddress(row.printed_url) },
  { id: "placements", header: "Placements", accessorFn: (row) => placementsLabel(row.placements) },
  {
    id: "goes_to",
    header: "Goes to",
    accessorFn: (row) => targetLabel(row.target_url),
    cell: ({ row }) => <span title={row.original.target_url}>{targetLabel(row.original.target_url)}</span>,
  },
  { id: "status", header: "Status", accessorFn: (row) => statusLabel(row.status), cell: ({ row }) => <StatusPill status={row.original.status} /> },
];

export function QrCodesTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const open = str(controls.values[Q_CODE]);
  if (open) return <QrCodeDetail controls={controls} id={open} areaName={areaName} />;
  const releases = str(controls.values[Q_VIEW]) === VIEW_RELEASES;
  return (
    <div className="flex flex-col gap-4">
      <FilterBar
        layout="segmented"
        filters={[VIEW_SPEC]}
        values={controls.values}
        onChange={(next) => controls.onChange({ ...next, [Q_CODE]: null, [Q_RELEASE]: null })}
      />
      {releases ? <ReleasesView controls={controls} areaName={areaName} /> : <QrCodeList controls={controls} areaName={areaName} />}
    </div>
  );
}

function QrCodeList({ controls, areaName }: { controls: Controls; areaName: string }) {
  const codes = useQuery({ queryKey: qrCodesKey(DEFAULT_DAYS), queryFn: () => getCodes(DEFAULT_DAYS) });
  const places = asList(controls.values[Q_PLACE]);
  const status = str(controls.values[Q_STATUS]);
  const openCode = (id: string) => controls.onChange({ ...controls.values, [Q_CODE]: id });

  if (codes.isError) {
    return (
      <Alert>
        <AlertTitle>QR Codes unavailable</AlertTitle>
        <AlertDescription>{qrReadMessage(codes.error, areaName)}</AlertDescription>
      </Alert>
    );
  }

  const rows = filterCodes(codes.data?.data.codes ?? [], places, status);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <FilterBar layout="segmented" filters={[STATUS_SPEC]} values={controls.values} onChange={controls.onChange} />
        <MultiChips
          label="Placement (any of)"
          options={PLACEMENT_OPTIONS}
          selected={places}
          onChange={(next) => controls.onChange({ ...controls.values, [Q_PLACE]: next })}
        />
        <div className="ml-auto">
          <NewCodeButton onCreated={openCode} />
        </div>
      </div>
      <DataTable
        columns={CODE_COLUMNS}
        rows={rows}
        pending={codes.isPending}
        page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
        onPageChange={() => {}}
        onRowClick={(row) => openCode(row.id)}
        emptyMessage="No codes match these filters."
        caption="Printed QR codes"
      />
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 md:grid-cols-[10rem_1fr] md:gap-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-base break-words whitespace-pre-wrap text-ks-ink">{children}</dd>
    </div>
  );
}

function QrCodeDetail({ controls, id, areaName }: { controls: Controls; id: string; areaName: string }) {
  const detail = useQuery({ queryKey: qrCodeKey(id, DEFAULT_DAYS), queryFn: () => getCode(id, DEFAULT_DAYS) });
  const back = () => controls.onChange({ ...controls.values, [Q_CODE]: null });

  if (detail.isError && !detail.data) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" data-nonmutating className="self-start" onClick={back}>
          ← Codes
        </Button>
        <Alert>
          <AlertTitle>Code unavailable</AlertTitle>
          <AlertDescription>{qrReadMessage(detail.error, areaName)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const data = detail.data?.data;
  if (!data) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const { code } = data;
  const address = printedAddress(code.printed_url);
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" data-nonmutating onClick={back}>
            ← Codes
          </Button>
          <h2 className="text-lg font-semibold text-ks-ink">{code.label}</h2>
          <StatusPill status={code.status} />
          <Button asChild size="sm" variant="outline" className="ml-auto text-foreground">
            <a href={imageUrl(code.id)} download={`qr-${code.id}.svg`} data-nonmutating>
              Download QR (SVG)
            </a>
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base text-ks-ink">{address}</span>
          <CopyButton text={code.printed_url} />
        </div>
        {code.status === "draft" && (
          <p className="text-sm text-ks-ink">Not live yet. Printed codes pointing here go to the old site.</p>
        )}
        <StatusActions code={code} />
      </header>

      <GoesTo data={data} controls={controls} />

      <Details code={code} />

      <History changes={data.changes} />
    </div>
  );
}

function GoesTo({ data, controls }: { data: QrCodeDetailData; controls: Controls }) {
  const me = useQrMe();
  const { code } = data;
  const pending = data.pending_release ?? null;
  const openRelease = () => {
    if (pending) controls.onChange({ ...controls.values, [Q_CODE]: null, [Q_VIEW]: VIEW_RELEASES, [Q_RELEASE]: String(pending.id) });
  };
  return (
    <section aria-label="Goes to" className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-ks-ink">Goes to</h3>
      <a href={code.target_url} target="_blank" rel="noopener noreferrer" className="break-all text-ks-purple underline-offset-2 hover:underline">
        {code.target_url}
      </a>
      {pending && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-ks-ink" data-testid="qr-pending-release">
          <span className="break-all">
            Draft change in {pending.name}: will go to {pending.target_url} when published
          </span>
          <Button size="sm" variant="outline" data-nonmutating className="text-foreground" onClick={openRelease}>
            Open release
          </Button>
          {me?.can_edit && pending.preview_url && <CopyButton text={pending.preview_url} label="Copy test link" />}
        </div>
      )}
      <div>
        <ChangeDestinationButton code={code} />
      </div>
    </section>
  );
}

function Details({ code }: { code: QrCode }) {
  const me = useQrMe();
  const [editing, setEditing] = useState(false);
  return (
    <section aria-label="Details" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold text-ks-ink">Details</h3>
        {me?.can_edit && !editing && (
          <Button size="sm" variant="outline" className="text-foreground" onClick={() => setEditing(true)}>
            Edit details
          </Button>
        )}
      </div>
      {editing ? (
        <EditDetails code={code} onClose={() => setEditing(false)} />
      ) : (
        <dl className="flex flex-col gap-3">
          <DetailRow label="Placements">{placementsLabel(code.placements)}</DetailRow>
          <DetailRow label="Product">{code.product ?? "–"}</DetailRow>
          <DetailRow label="Campaign">{code.campaign ?? "–"}</DetailRow>
          <DetailRow label="Print run">{code.print_run ?? "–"}</DetailRow>
          <DetailRow label="Notes">{code.notes ?? "–"}</DetailRow>
          <DetailRow label="Fallback address">{code.fallback_url}</DetailRow>
        </dl>
      )}
    </section>
  );
}

function History({ changes }: { changes: QrCodeDetailData["changes"] }) {
  return (
    <section aria-label="History" className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-ks-ink">History</h3>
      {changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No changes yet.</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm text-ks-ink">
          {changes.map((change, index) => (
            <li key={`${change.changed_at}-${change.field}-${index}`} className="break-words">
              {changeLine(change)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
